import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";

// Apple-style scroll-scrubbed frame sequence built from the brand transformation video.
// DESIGN (sketch) -> INSTALL (raw build) -> DELIVER (finished kitchen).
const FRAME_COUNT = 120;
// The animation is fully "arrived" by ~frame 116; the last few frames are a static
// hold. End the scrub there so the scroll lands on the finished kitchen with no dead hold.
const END_FRAME = 115; // 0-based index of the last frame to show while scrubbing
const framePath = (i) => `/hero/frames/f_${String(i + 1).padStart(3, "0")}.jpg`;
const clamp = (x, a, b) => Math.max(a, Math.min(b, x));

function drawCover(ctx, img, cw, ch) {
  if (!img || !img.width) return;
  const ir = img.width / img.height;
  const cr = cw / ch;
  let dw, dh, dx, dy;
  if (cr > ir) {
    dw = cw;
    dh = cw / ir;
    dx = 0;
    dy = (ch - dh) / 2;
  } else {
    dh = ch;
    dw = ch * ir;
    dx = (cw - dw) / 2;
    dy = 0;
  }
  ctx.clearRect(0, 0, cw, ch);
  ctx.drawImage(img, dx, dy, dw, dh);
}

export default function Hero() {
  const sectionRef = useRef(null);
  const canvasRef = useRef(null);
  const imagesRef = useRef([]);
  const frameRef = useRef(0);
  const rafRef = useRef(0);
  const [loaded, setLoaded] = useState(0);
  const [ready, setReady] = useState(false);

  // Preload every frame
  useEffect(() => {
    let count = 0;
    const imgs = new Array(FRAME_COUNT);
    for (let i = 0; i < FRAME_COUNT; i++) {
      const img = new Image();
      img.src = framePath(i);
      img.onload = img.onerror = () => {
        count += 1;
        setLoaded(count);
        if (count === FRAME_COUNT) setReady(true);
      };
      imgs[i] = img;
    }
    imagesRef.current = imgs;
  }, []);

  const render = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    drawCover(ctx, imagesRef.current[frameRef.current], canvas.width, canvas.height);
  };

  // Deterministic scroll progress from the section's position (immune to Lenis)
  const update = () => {
    const el = sectionRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const total = rect.height - window.innerHeight;
    const progress = total > 0 ? clamp(-rect.top / total, 0, 1) : 0;
    const target = Math.round(progress * END_FRAME);
    if (target !== frameRef.current) {
      frameRef.current = target;
      render();
    }
  };

  const onScroll = () => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(update);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(window.innerWidth * dpr);
      canvas.height = Math.floor(window.innerHeight * dpr);
      canvas.style.width = "100%";
      canvas.style.height = "100%";
      update();
      render();
    };
    resize();
    window.addEventListener("resize", resize);
    window.addEventListener("scroll", onScroll, { passive: true });
    if (window.__lenis) window.__lenis.on("scroll", onScroll);
    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("scroll", onScroll);
      if (window.__lenis) window.__lenis.off("scroll", onScroll);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Draw frames as they decode so there is never a blank canvas
  useEffect(() => {
    if (loaded > 0) render();
  }, [loaded]);

  const pct = Math.round((loaded / FRAME_COUNT) * 100);

  return (
    <section
      ref={sectionRef}
      className="relative h-[320vh] bg-navy"
      data-testid="hero-section"
      id="hero"
    >
      <div className="sticky top-0 h-screen w-screen overflow-hidden bg-navy">
        {/* SEO / accessibility heading — visually hidden, does not change the design */}
        <h1 className="sr-only">
          DIDIT — Home Interiors &amp; Modular Interiors in Delhi NCR
        </h1>
        {/* Full-bleed scroll-scrubbed canvas */}
        <canvas
          ref={canvasRef}
          data-testid="hero-canvas"
          role="img"
          aria-label="DIDIT home interior transformation — from 2D design to a fully installed and delivered modular kitchen"
          className="absolute inset-0 h-full w-full"
        />


        {/* Loader */}
        <motion.div
          initial={{ opacity: 1 }}
          animate={{ opacity: ready ? 0 : 1 }}
          transition={{ duration: 0.6 }}
          style={{ pointerEvents: ready ? "none" : "auto" }}
          className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-navy text-warm"
          data-testid="hero-loader"
        >
          <span className="font-display text-2xl md:text-3xl tracking-[0.4em] uppercase">
            DIDIT
          </span>
          <div className="mt-6 h-px w-40 md:w-56 bg-warm/20 overflow-hidden">
            <div
              className="h-full bg-terracotta transition-[width] duration-200"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="mt-3 text-[11px] tracking-[0.3em] text-warm/50">{pct}%</span>
        </motion.div>

        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: ready ? 1 : 0 }}
          transition={{ delay: 0.2 }}
          className="absolute top-28 left-5 md:left-10 z-20"
        >
          <p className="text-[11px] md:text-xs tracking-[0.35em] uppercase text-terracotta font-semibold drop-shadow">
            DIDIT · Interiors, end to end
          </p>
        </motion.div>

        {/* Scroll hint */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: ready ? [0, 1, 0.4] : 0 }}
          transition={{ delay: 0.6, duration: 2, repeat: Infinity, repeatType: "reverse" }}
          className="absolute bottom-8 right-6 md:right-10 z-20 hidden md:flex items-center gap-2 text-warm/70 text-xs tracking-widest uppercase"
        >
          Scroll <ArrowDown size={14} />
        </motion.div>
      </div>
    </section>
  );
}
